// src/v2/leases/LeaseDrawerParts.jsx
//
// Task #9096 — presentational pieces of the lease record drawer: header strip,
// identity, unavailable / error / loading states. Strip figures reuse the full
// reader's bases (leaseAnnualHeadlines, currentLeaseFootprint,
// leaseAreaPresentation, expiryDisplay). Failed supporting reads render as
// "Couldn't load", never as confirmed absence.
import { FileText } from "lucide-react";
import { T } from "../../app/tokens.js";
import { AuthError } from "../../data/errors.js";
import { fmtMoney, fmtDate, formatFootprintSqftFull } from "../../utils/format.js";
import { drawerAnnualRent } from "./drawerMoney.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { monthlyPayableEvidence } from "../../../shared/monthlyPayable.js";
import { leaseAreaPresentation } from "../../features/leases/leaseAreaPresentation.js";
import { isPastExpiryDate } from "../../features/leases/expiryDisplay.js";
import { LEASE_UNAVAILABLE_COPY, leaseIdentityLine } from "./leaseDrawerFinancials.js";
import { selectRecordDateEvents } from "../../features/leases/recordDateEvents.js";

export const RECENTLY_DELETED_HREF = "/admin?section=recently_deleted";
const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.cardShadow, minWidth: 0 };
const eyebrow = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".08em", textTransform: "uppercase", color: T.mid };
const ghostBtn = {
  display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 10px", border: `1px solid ${T.border}`,
  borderRadius: 8, background: T.card, color: T.dark, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit",
};
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function displayBucket(lease) {
  if (lease?.leaseStatus != null) return lease.leaseStatus;
  return { active: "in_use", holdover: "in_use", in_negotiation: "in_use", draft: "draft", sublease_out: "sublease_out", expired: "expired", terminated: "expired" }[lease?.status] ?? null;
}

/** "{Market} · {alias}" from header + detail + office; never invented. */
export function leaseHeaderTitle(headerLease, detail, location) {
  return leaseIdentityLine({
    market: location?.market ?? headerLease?.market ?? null,
    city: headerLease?.city ?? detail?.city ?? null,
    locationAlias: headerLease?.locationAlias ?? detail?.locationAlias ?? location?.locationAlias ?? null,
    premises: null,
  }) || headerLease?.id || "Lease";
}

export function nextCriticalDate(dates, today = new Date()) {
  const t = isoDay(today);
  return (Array.isArray(dates) ? dates : [])
    .filter((d) => d?.dueDate && String(d.dueDate).slice(0, 10) >= t && !d.completedAt)
    .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))[0] ?? null;
}

/** Date-only expiry state: the expiry day itself is not past. */
export function expiryState(endDate, today = new Date()) {
  const end = endDate ? String(endDate).slice(0, 10) : null;
  if (!end || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return { end: null, past: false, yearsLeft: null };
  if (end.startsWith("9999")) return { end: null, past: false, yearsLeft: null, ongoing: true };
  const t = isoDay(today);
  const days = (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${t}T00:00:00Z`)) / 864e5;
  return { end, past: end < t, yearsLeft: days / 365.25 };
}

function StripCell({ label, value, sub, testId, accent, subAccent, tooltip, calculated = false, unverified = false }) {
  return (
    <div data-testid={testId} title={tooltip} tabIndex={tooltip ? 0 : undefined} style={{ padding: "0 14px", height: 68, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, borderLeft: `1px solid ${T.hairline ?? T.border}` }}>
      <div style={{ ...eyebrow, display: "flex", alignItems: "center", gap: 4, letterSpacing: unverified ? ".02em" : eyebrow.letterSpacing }}>{label}
        {unverified && <span data-testid={`${testId}-unverified`} style={{ padding: "0 3px", border: `1px dotted ${T.mid}`, borderRadius: 3, fontSize: T.fsXxxs, color: T.mid, textTransform: "none", letterSpacing: 0, fontWeight: T.fwRegular }}>unverified</span>}
      </div>
      <div data-testid={`${testId}-value`} style={{ marginTop: 3, fontSize: T.fsLg, lineHeight: "22px", fontWeight: T.fwBold, color: accent ?? T.dark, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}
        {calculated && <span data-testid={`${testId}-calc`} style={{ marginLeft: 4, padding: "0 3px", border: `1px dotted ${T.mid}`, borderRadius: 3, fontSize: T.fsXxxs, color: T.mid, verticalAlign: "middle" }}>calc</span>}
      </div>
      <div title={tooltip || sub || undefined} style={{ marginTop: 1, lineHeight: "14px", fontSize: T.fsXxs, color: subAccent ?? T.mid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub || "\u00a0"}</div>
    </div>
  );
}

/** Per-sf subline from the same headline amount and current gross footprint; null when either is unknown. */
export function perSf(annual, currency, lease) {
  const gross = lease ? currentLeaseFootprint(lease)?.grossSf : null;
  if (annual == null || !Number.isFinite(annual) || !gross || !(gross > 0)) return null;
  return `${fmtMoney(annual / gross, currency, { decimals: 2, code: false })}/sf`;
}

/** Date-only percent of term elapsed; null without a valid start before end. */
export function elapsedPct(start, end, today = new Date()) {
  const s = start ? String(start).slice(0, 10) : null;
  if (!s || !end || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const a = Date.parse(`${s}T00:00:00Z`); const b = Date.parse(`${end}T00:00:00Z`); const t = Date.parse(`${isoDay(today)}T00:00:00Z`);
  if (!(b > a)) return null;
  return `${Math.round(Math.min(1, Math.max(0, (t - a) / (b - a))) * 100)}% elapsed`;
}

export function LeaseHeaderStrip({ lease, support, headerLease, displayCcy, rates, translucent = false, today = new Date() }) {
  const failed = new Set((support?.readErrors || []).map((e) => e.label));
  const pending = !support;
  const year = today.getFullYear();
  const red = T.ragRed ?? T.red;

  let rent = { value: "…", sub: null };
  if (failed.has("Lease summary") || (lease && monthlyPayableEvidence(lease).state === "invalid")) rent = { value: "Couldn't load", sub: "Retry below" };
  else if (lease) {
    const h = drawerAnnualRent(lease, lease.computed, today, displayCcy || lease.currency || "USD", rates);
    rent = h.amount != null
      ? { value: h.text, tooltip: h.tooltip,
          sub: [year, perSf(h.amount, h.currency, lease)].filter(Boolean).join(" · ") }
      : { value: "–", sub: `No ${year} rent in source` };
  } else if (!pending) rent = { value: "–", sub: "No financial detail" };

  const src = lease ?? headerLease;
  const gross = currentLeaseFootprint(src)?.grossSf ?? null;
  const areas = Array.isArray(src?.areas) ? leaseAreaPresentation(src, today).activeCount : null;

  const exp = expiryState(src?.endDate ?? src?.leaseEnd, today);
  const past = exp.end ? isPastExpiryDate(exp.end, today) && exp.past : false;
  const selected = support ? selectRecordDateEvents({ ...headerLease, ...lease }, support.criticalDates, today) : null;
  const elapsedStart = selected?.events.find((event) => event.type === "commencement")?.date ?? src?.startDate ?? src?.leaseStart;

  let next = { value: "…", sub: null };
  if (failed.has("Critical dates")) next = { value: "Couldn't load", sub: "Retry below" };
  else if (support) {
    const n = selected.next;
    next = n ? { value: n.provenance === "calculated"
        ? new Date(`${n.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : fmtDate(n.date),
      calculated: n.provenance === "calculated",
      unverified: n.provenance === "ambiguous",
      sub: n.provenance === "ambiguous" ? `${n.label} · source unverified` : n.provenance === "calculated" ? `${n.type === "renewal_notice" ? "Renewal notice" : n.label} · calculated` : n.label, tooltip: n.disclosure || n.label }
      : { value: "None", sub: selected.ambiguities.length ? "Date sources need review" : "No upcoming critical date" };
  }

  return (
    <div data-testid="lease-record-strip" className="lrd-strip"
      style={{ ...card, height: 68, boxSizing: "border-box", overflow: "hidden", background: translucent ? T.coverPanelBg ?? T.card : T.card, backdropFilter: translucent ? T.coverPanelBlur : undefined }}>
      <StripCell testId="lease-strip-rent" label="Annual rent" value={rent.value} sub={rent.sub} tooltip={rent.tooltip} accent={failed.has("Lease summary") ? T.mid : undefined} />
      <StripCell testId="lease-strip-size" label="Size" value={gross != null ? formatFootprintSqftFull(gross) : "–"}
        sub={areas != null ? `${areas} active area${areas === 1 ? "" : "s"}` : gross == null ? "No area recorded" : null} />
      <StripCell testId="lease-strip-expires" label="Expires" accent={past ? red : undefined} subAccent={past ? red : undefined}
        value={exp.end ? fmtDate(exp.end) : exp.ongoing ? "Ongoing" : "–"}
        sub={exp.end ? (past ? "Past expiry" : [`${Math.max(0, exp.yearsLeft).toFixed(1)} yrs left`, elapsedPct(elapsedStart, exp.end, today)].filter(Boolean).join(" · ")) : exp.ongoing ? "Month-to-month / no fixed end" : "No expiry recorded"} />
      <StripCell testId="lease-strip-next" label="Next date" value={next.value} calculated={next.calculated} unverified={next.unverified} sub={next.sub} tooltip={next.tooltip} accent={failed.has("Critical dates") ? T.mid : undefined} />
    </div>
  );
}

export function LeaseDrawerSkeleton({ testId = "lease-record-loading" }) {
  const blk = (h, w = "100%") => <div style={{ height: h, width: w, borderRadius: 8, background: T.lightGray, marginBottom: 10 }} />;
  return <div data-testid={testId} aria-busy="true" aria-label="Loading lease">{blk(14, "22%")}{blk(26, "58%")}{blk(64)}{blk(180)}{blk(120)}</div>;
}

export function LeaseUnavailable({ isAdmin, onFollow }) {
  return (
    <div data-testid="lease-record-unavailable" style={{ ...card, padding: "36px 28px", marginTop: 24, textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 12, margin: "0 auto 14px", display: "grid", placeItems: "center", background: T.lightGray, color: T.mid }}>
        <FileText size={22} />
      </div>
      <p style={{ margin: 0, fontSize: T.fsBase, color: T.dark, fontWeight: T.fwSemibold, lineHeight: 1.45 }}>{LEASE_UNAVAILABLE_COPY}</p>
      {isAdmin && (
        <button type="button" data-testid="lease-record-recently-deleted" onClick={() => onFollow?.(RECENTLY_DELETED_HREF)}
          style={{ border: 0, background: "transparent", padding: 0, marginTop: 14, color: T.blue, fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit" }}>
          Open Recently Deleted →
        </button>
      )}
    </div>
  );
}

export function LeaseLoadError({ error, onRetry }) {
  const auth = error instanceof AuthError || error?.status === 401 || error?.status === 403;
  return (
    <div role="alert" data-testid={auth ? "lease-record-auth-error" : "lease-record-error"} style={{ ...card, padding: 22, marginTop: 24 }}>
      <p style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {auth ? "You don't have access to this lease right now." : "This lease couldn't be loaded."}
      </p>
      <p style={{ margin: "6px 0 14px", fontSize: T.fsSm, color: T.mid }}>
        {auth ? "Your session may have expired or your role changed. Sign in again, then retry." : "The request failed before the record arrived. Check your connection and try again."}
      </p>
      {error?.message && <p data-testid="lease-record-error-message" style={{ margin: "0 0 14px", fontSize: T.fsSm, color: T.dark, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", overflowWrap: "anywhere" }}>{error.message}</p>}
      <button type="button" data-testid="lease-record-retry" onClick={onRetry} style={ghostBtn}>Retry</button>
    </div>
  );
}
